import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment,
  List, ListItemAvatar, ListItemButton, ListItemText, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import unifiedApi, { buildApiUrl } from '../../utils/unifiedApi';
import { useChat } from '../../contexts/ChatContext';
import { initialsFor } from '../../utils/chatFormat';

export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

const fullName = (member) => `${member.firstName || ''} ${member.lastName || ''}`.trim();

/** Search the organization's members and open a direct conversation with one of them. */
const NewDirectMessageDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { openDirectChat } = useChat();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [opening, setOpening] = useState(null);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setError(null);
      setOpening(null);
    }
  }, [open]);

  useEffect(() => {
    const trimmed = query.trim();
    const request = ++requestRef.current;
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await unifiedApi.searchChatMembers(trimmed);
        if (request !== requestRef.current) return;
        setResults(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        if (request !== requestRef.current) return;
        setResults([]);
        setError(err.message || 'Failed to search members.');
      } finally {
        if (request === requestRef.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const start = async (member) => {
    setOpening(member.userTenantId);
    setError(null);
    try {
      const channel = await openDirectChat(member.userTenantId);
      if (!channel) {
        setError(`${fullName(member)} can't be messaged.`);
        return;
      }
      onClose();
      navigate(`/chat/${channel.id}?compose=1`);
    } catch (err) {
      setError(err.message || 'Failed to open the conversation.');
    } finally {
      setOpening(null);
    }
  };

  const trimmed = query.trim();
  let hint = null;
  if (trimmed.length < MIN_SEARCH_LENGTH) hint = 'Type at least 2 characters of a name.';
  else if (!searching && results.length === 0 && !error) hint = 'No members found.';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={isMobile} aria-labelledby="new-direct-message-title">
      <DialogTitle id="new-direct-message-title">New message</DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Search members"
          placeholder="Name"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end"><CircularProgress size={18} aria-label="Searching" /></InputAdornment>
            ) : null,
          }}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {hint && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{hint}</Typography>
        )}
        {results.length > 0 && (
          <List aria-label="Matching members" sx={{ mt: 1 }}>
            {results.map((member) => {
              const name = fullName(member);
              return (
                <ListItemButton
                  key={member.userTenantId}
                  onClick={() => start(member)}
                  disabled={opening !== null}
                  aria-label={`Message ${name}`}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar src={member.photoUrl ? buildApiUrl(member.photoUrl) : undefined} alt={name}>
                      {initialsFor(name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={name} primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }} />
                  {opening === member.userTenantId && (
                    <Box sx={{ ml: 1, display: 'flex' }}><CircularProgress size={18} /></Box>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewDirectMessageDialog;
